import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { IClassroomRequirement } from 'interfaces/classrooms';
import { ICourseRequirement, IDataNode } from 'interfaces/courses';
import { IRequirement } from 'interfaces/requirements';
import { IUnit } from 'interfaces/unit';
import {
  RequirementKind,
  SelectRequirement
} from '../form-items/SelectRequirement';
import { createRequirement, updateRequirement } from 'requests/requirements';
import {
  getRequirementWeeks,
  parseAvailabilityDate,
  toAvailabilityDate,
  weeksToMinutes
} from 'services/date';
import { useModalReturnType, useNotification } from '@refinedev/core';

interface Props {
  unit?: IUnit;
  modal: useModalReturnType;
  node: IDataNode;
  onUpdate: (
    node: IDataNode,
    requirement?: ICourseRequirement | IClassroomRequirement | null
  ) => void;
}

const RequirementNodeModal: React.FC<Props> = ({
  unit,
  node,
  modal,
  onUpdate
}) => {
  const { open } = useNotification();
  const [form] = Form.useForm();
  const [requirementKind, setRequirementKind] =
    useState<RequirementKind>('week');
  const [requirement, setRequirement] = useState<IRequirement | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const requirements =
      unit?.requirements.filter(
        requirement => requirement.resource_id === node.key.toString()
      ) || [];

    if (requirements.length > 0 && requirements[0].rules.length > 0) {
      const rule = requirements[0].rules[requirements[0].rules.length - 1];

      const requirementWeeks = getRequirementWeeks(+rule?.value);

      if (rule?.kind === 'duration_minutes') {
        setRequirementKind('week');
        form.setFieldsValue({ duration: requirementWeeks });
      }

      if (rule?.kind === 'date') {
        const date = parseAvailabilityDate(rule?.value);
        setRequirementKind('date');
        form.setFieldsValue({ duration: date });
      }

      setRequirement(requirements[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  const onClose = () => {
    modal.close();
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onSubmit = async () => {
    try {
      setLoading(true);
      const { name: title, duration } = await form.validateFields();

      const updatedNode = {
        ...node,
        title: title
      };

      if (!unit || duration === '' || typeof duration === 'undefined') {
        onUpdate(updatedNode);
        return;
      }

      if (unit) {
        const kind = requirementKind === 'date' ? 'date' : 'duration_minutes';

        let selectedDuration: number | string = '';

        if (requirementKind === 'date') {
          selectedDuration = toAvailabilityDate(duration);
        }

        if (requirementKind === 'week') {
          selectedDuration = weeksToMinutes(duration);
        }

        const resourceParams =
          unit.kind === 'classroom'
            ? { classroomId: unit.id }
            : { courseId: unit.id };
        const createOrUpdateRequirement = requirement?.id
          ? updateRequirement({
              ...resourceParams,
              requirementId: requirement.id,
              nodeId: node.key.toString(),
              duration: selectedDuration,
              key: kind === 'date' ? 'current_date' : 'possession_date',
              kind
            })
          : createRequirement({
              ...resourceParams,
              nodeId: node.key.toString(),
              duration: selectedDuration,
              key: kind === 'date' ? 'current_date' : 'possession_date',
              kind
            });

        createOrUpdateRequirement.then(res => {
          onUpdate(updatedNode, res);
        });
      }
    } finally {
      open?.({
        type: 'success',
        description: 'Successo!',
        message: 'Regra de liberação adicionada com sucesso!'
      });
      setLoading(false);
      onClose();
    }
  };

  const handleInputChange = useCallback(
    (value: RequirementKind) => {
      setRequirementKind(value);
      form.setFieldsValue({ duration: '' });
    },
    [form]
  );

  return (
    <Modal
      title="Editar seção"
      width={1000}
      visible={modal.visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          loading={loading}
          onClick={form.submit}
        >
          Salvar
        </Button>
      ]}
    >
      <Form
        form={form}
        initialValues={{
          name: node.title
        }}
        layout="vertical"
        preserve={false}
        onFinish={onSubmit}
      >
        <Form.Item label="Nome" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        {unit && (
          <>
            <Typography.Title level={5} className="pb-2">
              Regra de liberação
            </Typography.Title>

            <SelectRequirement
              kind={requirementKind}
              defaultValue={0}
              onSelect={handleInputChange}
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default RequirementNodeModal;
